import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <Link to="/">
          <Button variant="ghost" className="mb-8">
            <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
            Back to Home
          </Button>
        </Link>

        <h1 className="text-4xl font-bold mb-4">Cookie and Local Storage Policy</h1>
        <p className="text-muted-foreground mb-8">Effective date: August 21, 2026</p>

        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-4">Summary</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Brand Equity Analyzer does not use advertising cookies, analytics cookies, or cross-site
              tracking technology. Because of that, there is no consent banner and no non-essential categories to
              turn on or off — the only browser storage we use is strictly necessary to make the app work. You can
              still review and record your privacy choices, including an opt-out of any sale or sharing of
              personal information, using the <strong>Cookie Settings</strong> link in the site footer. If we
              later add analytics or advertising technology, we will publish a full preference manager and ask for
              your choice before those tags load.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Strictly necessary storage we use</h2>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>
                <strong>Authentication session</strong> (browser local storage, set by our backend provider
                Supabase): keeps you signed in between page loads. Persists until you sign out or the session
                expires.
              </li>
              <li>
                <strong>Guest and form-progress keys</strong> (browser local storage): remember whether you are in
                guest mode and carry your business details, detected social profiles, and current report
                reference between the steps of the analysis flow. Cleared when you exit guest mode, sign out, or
                clear your browser storage.
              </li>
              <li>
                <strong>Bot protection</strong>: our public forms use an in-app challenge plus a hidden field
                that only automated scripts fill in, and our backend applies rate limits. The challenge answer is
                held in the page while you complete the form and is not stored afterwards.
              </li>
              <li>
                <strong>Hosting and security</strong>: our hosting provider may set strictly necessary cookies or
                process request data to deliver the site securely.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Your controls</h2>
            <p className="text-muted-foreground leading-relaxed">
              You can clear or block browser storage at any time in your browser settings. Blocking the storage
              above will sign you out and will prevent the multi-step analysis form from carrying your answers
              forward, because that storage is essential to those features.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Use <strong>Cookie Settings</strong> in the site footer to record an opt-out of any sale or sharing
              of personal information; the choice is versioned, saved on your browser, and can be reset there at
              any time. The site also reads the Global Privacy Control (GPC) signal and applies the opt-out
              automatically when your browser sends one. We do not sell personal information for money and we do
              not use it for targeted advertising, but because state privacy laws define "sale" and "sharing"
              broadly, our{" "}
              <Link to="/privacy" className="underline text-brand-700">Privacy Policy</Link> explains which
              disclosures to our providers could fall within those definitions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">More information</h2>
            <p className="text-muted-foreground leading-relaxed">
              For details on what we collect and why, see our{" "}
              <Link to="/privacy" className="underline text-brand-700">Privacy Policy</Link>. Questions:{" "}
              <a href="mailto:privacy@topservdigital.com" className="underline text-brand-700">
                privacy@topservdigital.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
